/**
 * KA-49 §3 — mảnh dùng chung cho MỌI biểu đồ miền (recharts): dải gradient đậm mép trên → nhạt xuống, mẫu gạch chéo "thiếu số",
 *   kiểu khung chú thích nổi. Phần thuần (chuẩn hoá null, khoảng thiếu) ở `lib/mien.ts`.
 * ⚠ `defsMien()` là HÀM trả `<defs>` (không phải component): recharts chỉ vẽ con là phần tử SVG gốc hoặc phần tử của nó —
 *   một component lạ đặt trong `<AreaChart>` bị bỏ qua im lặng.
 * ⚠ id gradient phải duy nhất theo từng biểu đồ (một trang có nhiều biểu đồ) ⇒ tiền tố từ `useId()`.
 */
import { useId, type CSSProperties } from 'react';
import { Area, AreaChart, CartesianGrid, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { MAU_LOP, diemLe, duLieuMien, khoangThieu } from '../../lib/mien';

/** Tiền tố id an toàn cho SVG (useId trả ":r1:" — dấu ":" làm `url(#…)` hỏng). */
export function useIdMien(): string {
  return 'm' + useId().replace(/[^a-zA-Z0-9]/g, '');
}

/** `url(#…)` của lớp thứ i; `gach` = mẫu gạch chéo. */
export const toMien = (p: string, i: number) => `url(#${p}-${i})`;
export const toGach = (p: string) => `url(#${p}-gach)`;
/** Màu viền trên của lớp thứ i (token). */
export const vienMien = (i: number, mau: readonly string[] = MAU_LOP) => `rgb(var(${mau[i % mau.length]}))`;

/** `<defs>` cho n lớp: đỉnh đục `dinh`, đáy gần trong suốt — đúng dải `nlA/nlB/nlC` của mẫu `mau_nang_luong`. */
export function defsMien(p: string, n: number, mau: readonly string[] = MAU_LOP, dinh = 0.6) {
  return (
    <defs>
      {Array.from({ length: n }, (_, i) => (
        <linearGradient key={i} id={`${p}-${i}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={`rgb(var(${mau[i % mau.length]}))`} stopOpacity={dinh} />
          <stop offset="1" stopColor={`rgb(var(${mau[i % mau.length]}))`} stopOpacity={0.04} />
        </linearGradient>
      ))}
      <pattern id={`${p}-gach`} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="8" height="8" fill="rgb(var(--chu-3))" fillOpacity={0.04} />
        <line x1="0" y1="0" x2="0" y2="8" stroke="rgb(var(--chu-3))" strokeOpacity={0.35} strokeWidth="3" />
      </pattern>
    </defs>
  );
}

/** Khung chú thích nổi khi rê chuột — cùng hình `.khoi-noi`. */
export const KIEU_GOI_Y: { contentStyle: CSSProperties; labelStyle: CSSProperties; itemStyle: CSSProperties } = {
  contentStyle: { background: 'rgb(var(--nen-ben) / .92)', border: '1px solid rgb(var(--vien-manh))', borderRadius: 10, boxShadow: 'var(--bong-noi)', fontSize: 12 },
  labelStyle: { color: 'rgb(var(--chu-1))', fontWeight: 600 },
  itemStyle: { color: 'rgb(var(--chu-2))' },
};
export const TRUC = { fill: 'rgb(var(--chu-2))', fontSize: 10 } as const;

/* ───────────── biểu đồ miền theo kỳ (ngày / tháng / năm) — một hoặc nhiều lớp ───────────── */

/** `mau` = tên token (mặc định theo thứ tự MAU_LOP) · `gach` = viền nét đứt (lớp ƯỚC TÍNH, vd tiết kiệm nhờ dim) */
export interface LopMien { khoa: string; ten: string; mau?: string; gach?: boolean }

/**
 * Miền nhiều lớp theo trục PHÂN LOẠI (nhãn kỳ). `chong` ⇒ các lớp là phần của một tổng (stackId). Kỳ thiếu số: miền NGẮT
 * (`connectNulls={false}`) + dải gạch chéo "thiếu số" — không vẽ 0. `duongTb` (tuỳ chọn) = khoá của một đường gạch (vd TB 7 ngày).
 */
export function MienTheoKy({ ds, truc, lop, chong = false, cao = 220, donVi, duongTb, oDo, soLe = 1 }: {
  ds: Record<string, unknown>[]; truc: string; lop: LopMien[]; chong?: boolean; cao?: number; donVi: string;
  duongTb?: { khoa: string; ten: string }; oDo?: string;
  /** số chữ thập phân trên trục / chú thích */
  soLe?: number;
}) {
  const p = useIdMien();
  const khoa = lop.map((l) => l.khoa);
  const mau = lop.map((l, i) => l.mau ?? MAU_LOP[i % MAU_LOP.length]);
  const data = duLieuMien(ds, duongTb ? [...khoa, duongTb.khoa] : khoa);
  const thieu = khoangThieu(data, khoa, truc);
  return (
    <div className="w-full" style={{ height: cao }} data-o={oDo}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 6, right: 6, bottom: 0, left: -8 }}>
          {defsMien(p, lop.length, mau)}
          <CartesianGrid stroke="rgb(var(--vien))" vertical={false} />
          <XAxis dataKey={truc} tick={TRUC} tickLine={false} axisLine={{ stroke: 'rgb(var(--vien-manh))' }} minTickGap={16} />
          <YAxis tick={TRUC} width={48} tickLine={false} axisLine={false} tickFormatter={(v: number) => v.toLocaleString('vi-VN', { maximumFractionDigits: soLe })} />
          <Tooltip {...KIEU_GOI_Y} formatter={(v: unknown, ten: unknown) => [v == null ? '—' : `${Number(v).toLocaleString('vi-VN', { maximumFractionDigits: soLe })} ${donVi}`, String(ten)]} />
          {thieu.map((t) => (
            <ReferenceArea key={`${t.i0}`} x1={t.x1 as string} x2={t.x2 as string} fill={toGach(p)} fillOpacity={1} stroke="rgb(var(--vien-manh))" strokeDasharray="3 3"
              label={{ value: 'thiếu số', fill: 'rgb(var(--chu-2))', fontSize: 10, position: 'insideTop' }} ifOverflow="extendDomain" />
          ))}
          {lop.map((l, i) => (
            <Area key={l.khoa} dataKey={l.khoa} name={l.ten} type="linear" stackId={chong ? 'tong' : undefined}
              stroke={vienMien(i, mau)} strokeWidth={i === 0 ? 2 : 1.5} strokeDasharray={l.gach ? '5 4' : undefined} fill={toMien(p, i)} fillOpacity={1}
              connectNulls={false} isAnimationActive={false} activeDot={{ r: 3 }}
              dot={chamLe(diemLe(data, l.khoa), vienMien(i, mau))} />
          ))}
          {duongTb && (
            <Area dataKey={duongTb.khoa} name={duongTb.ten} type="linear" stroke="rgb(var(--tim))" strokeWidth={1.5} strokeDasharray="5 4"
              fill="none" connectNulls={false} isAnimationActive={false} dot={false} activeDot={false} />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Hàm `dot` của recharts: chỉ vẽ chấm ở điểm lẻ (xem `diemLe`). */
function chamLe(le: Set<number>, mau: string) {
  return (pr: { cx?: number; cy?: number; index?: number }) =>
    pr.index !== undefined && le.has(pr.index) && pr.cx !== undefined && pr.cy !== undefined
      ? <circle key={pr.index} cx={pr.cx} cy={pr.cy} r={3.5} fill={mau} stroke="rgb(var(--nen-1))" strokeWidth={1.5} />
      : <g key={pr.index} />;
}

/** KA-49 §5: đường xu hướng miền ở đáy ô số (mẫu `mau_nang_luong`) — null bỏ qua (không vẽ 0); < 2 điểm có số ⇒ không vẽ. */
export function Spark({ ds, mau = '--nhan' }: { ds: readonly (number | null)[]; mau?: string }) {
  const p = useIdMien();
  const co = ds.map((v, i) => ({ v, i })).filter((x): x is { v: number; i: number } => x.v !== null);
  if (co.length < 2) return null;
  const max = Math.max(...co.map((x) => x.v)), min = Math.min(...co.map((x) => x.v));
  const X = (i: number) => ((i * 120) / Math.max(1, ds.length - 1)).toFixed(1);
  const Y = (v: number) => (30 - ((v - min) / (max - min || 1)) * 22).toFixed(1);
  const duong = co.map((x, k) => `${k ? 'L' : 'M'}${X(x.i)},${Y(x.v)}`).join(' ');
  const mien = `${duong} L${X(co[co.length - 1].i)},34 L${X(co[0].i)},34 Z`;
  return (
    <svg viewBox="0 0 120 34" preserveAspectRatio="none" width="100%" height="34" className="op-spark" aria-hidden="true">
      <defs><linearGradient id={`${p}-s`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={`rgb(var(${mau}))`} stopOpacity="0.55" /><stop offset="1" stopColor={`rgb(var(${mau}))`} stopOpacity="0" /></linearGradient></defs>
      <path d={mien} fill={`url(#${p}-s)`} />
      <path d={duong} fill="none" stroke={`rgb(var(${mau}))`} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
